import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  fetchVoiceLinesSplit,
  resplitVoiceLines,
  scenarioVoiceMapQueryKey,
  voiceLinesSplitQueryKey,
  type VoiceLinesSplitChunk,
} from '../../../features/scenarios/api';
import { fetchMaterialContentBlob } from '../../../features/materials/api';
import { useToast } from '../../../hooks/useToast';
import { isStorageQuotaError, storageQuotaErrorMessage } from '../../../utils/storageQuotaError';
import { SegmentedTabs } from '../../common/SegmentedTabs';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { motionPlanQueryKey } from './hooks/useMotionPlan';
import { resolveAudioUrl } from './narrationUtils';
import { RangeAudioPlayer, type RangePlayback } from './narrationSplitPlayer';
import {
  changedLineIndexes,
  cutsFromRanges,
  formatSplitTime,
  moveCut,
  rangesFromCuts,
  waveHeightRatio,
} from './narrationSplit';
import own from './NarrationSplitEditorModal.module.css';

/** 拡大率 (1 = 枠の幅に全体が収まる) */
const ZOOM_LEVELS = [1, 2, 4, 8, 16];
/** 波形キャンバスの最大幅 (px)。これを超える拡大はしない */
const MAX_CANVAS_WIDTH = 16000;
const WAVEFORM_HEIGHT = 120;
/** 境界の試聴で前後に流す秒数 */
const BOUNDARY_PREVIEW_SEC = 1.5;
/** 矢印キーでの境界の移動量 (Shift で細かく) */
const NUDGE_SEC = 0.05;
const NUDGE_FINE_SEC = 0.01;

/**
 * 試聴用に分割前の音声のバイト列を取る。素材行経由 (GCS の CORS 事情を API 中継で吸収) を先に試し、
 * 取れなければ再生用 URL を直接 fetch する (CORS が許すときだけ成功する)
 */
async function loadChunkAudioBytes(chunk: VoiceLinesSplitChunk): Promise<ArrayBuffer> {
  try {
    const blob = await fetchMaterialContentBlob(chunk.full_material_id);
    return await blob.arrayBuffer();
  } catch (error) {
    const url = await resolveAudioUrl({
      id: chunk.full_material_id,
      preview_url: chunk.preview_url ?? undefined,
      source_url: chunk.source_url ?? undefined,
      storage_bucket: chunk.storage_bucket ?? undefined,
      storage_path: chunk.storage_path ?? undefined,
    });
    if (!url) throw error;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
    return await res.arrayBuffer();
  }
}

/**
 * ナレーションの分割位置を手動で調整するモーダル (行ベース)。
 *
 * まとめて読んだ分割前の音声 (塊全体) の波形の上に、行の区切り (境界) を縦線で重ね、ドラッグ・矢印キーで
 * 動かす。「適用」で境界が動いた行だけ BE で切り直し (TTS なし・ポイント消費なし)、その行を含むシーンの
 * ナレーションを組み立て直す。古い音声は各シーンの履歴に残る。
 */
export function NarrationSplitEditorModal({ scenarioId, onClose }: { scenarioId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: voiceLinesSplitQueryKey(scenarioId),
    queryFn: () => fetchVoiceLinesSplit(scenarioId),
    // 開くたびに最新の割り当てを取り直す (生成し直し・履歴からの選び直しを反映)
    staleTime: 0,
    gcTime: 0,
  });
  const chunks = data?.chunks ?? [];
  const [chunkIdx, setChunkIdx] = useState(0);
  const chunk = chunks[Math.min(chunkIdx, Math.max(0, chunks.length - 1))];

  return createPortal(
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card" style={{ maxWidth: 960, width: 'min(960px, calc(100vw - 32px))' }}>
        <div className="modal-header">
          <h3 className="modal-title">ナレーションの分割位置を調整</h3>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">✕</button>
        </div>
        {isLoading ? (
          <div className="modal-body"><p className={own.status}>波形を読み込み中…</p></div>
        ) : isError ? (
          <div className="modal-body"><p className={own.status}>分割前のナレーション音声を読み込めませんでした</p></div>
        ) : !chunk ? (
          <div className="modal-body"><p className={own.status}>まとめて生成したナレーションがありません</p></div>
        ) : (
          <>
            {chunks.length > 1 && (
              <div className={own.chunkTabs}>
                <SegmentedTabs
                  value={String(chunkIdx)}
                  onChange={(v) => setChunkIdx(Number(v))}
                  options={chunks.map((c, i) => ({ value: String(i), label: `パート${i + 1}（${c.lines.length}行）` }))}
                />
              </div>
            )}
            {/* パートを切り替えたら編集状態を作り直す */}
            <ChunkEditor key={chunk.full_material_id} scenarioId={scenarioId} chunk={chunk} onClose={onClose} />
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ChunkEditor({ scenarioId, chunk, onClose }: { scenarioId: string; chunk: VoiceLinesSplitChunk; onClose: () => void }) {
  const { refreshVoiceScenario } = useSceneHandlers();
  const queryClient = useQueryClient();
  const toast = useToast();
  const total = chunk.duration_seconds;
  const originalRanges = useMemo(() => chunk.lines.map((l) => l.range_sec), [chunk]);
  const initialCuts = useMemo(() => cutsFromRanges(originalRanges), [originalRanges]);
  const [cuts, setCuts] = useState<number[]>(initialCuts);
  const [selectedCut, setSelectedCut] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const missing = useMemo(() => chunk.lines.map((l) => l.missing), [chunk]);
  const ranges = useMemo(() => rangesFromCuts(cuts, total), [cuts, total]);
  const changed = useMemo(() => changedLineIndexes(originalRanges, cuts, total, missing), [originalRanges, cuts, total, missing]);
  const changedSet = useMemo(() => new Set(changed), [changed]);
  // 切り直す対象はシーン単位 (同じシーンの複数行が動いても 1 シーン)
  const changedSceneCount = useMemo(() => new Set(changed.map((i) => chunk.lines[i].scene_id)).size, [changed, chunk]);

  // --- レイアウト (枠の幅と拡大率から 1 秒あたりの px を決める) ---
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const maxZoomIdx = useMemo(() => {
    let idx = 0;
    while (idx < ZOOM_LEVELS.length - 1 && viewportWidth * ZOOM_LEVELS[idx + 1] <= MAX_CANVAS_WIDTH) idx++;
    return idx;
  }, [viewportWidth]);
  const zoom = ZOOM_LEVELS[Math.min(zoomIdx, maxZoomIdx)];
  const trackWidth = Math.max(1, Math.round(viewportWidth * zoom));
  const pxPerSec = total > 0 ? trackWidth / total : 0;

  // --- 波形の描画 ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || trackWidth <= 1) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(trackWidth * dpr);
    canvas.height = Math.round(WAVEFORM_HEIGHT * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, trackWidth, WAVEFORM_HEIGHT);
    const peaks = chunk.peaks;
    if (!peaks.length) return;
    const mid = WAVEFORM_HEIGHT / 2;
    const perPx = peaks.length / trackWidth;
    ctx.fillStyle = 'rgba(245, 241, 230, 0.6)';
    for (let x = 0; x < trackWidth; x++) {
      const from = Math.floor(x * perPx);
      const to = Math.max(from + 1, Math.floor((x + 1) * perPx));
      let peak = 0;
      for (let i = from; i < to && i < peaks.length; i++) if (peaks[i] > peak) peak = peaks[i];
      const h = Math.max(1, waveHeightRatio(peak) * (WAVEFORM_HEIGHT - 8));
      ctx.fillRect(x, mid - h / 2, 1, h);
    }
  }, [chunk.peaks, trackWidth]);

  // --- 再生 ---
  // Web Audio でサンプル精度の区間再生をする。<audio> の currentTime を rAF で監視して pause() する方式だと
  // 境界を数十ms超えてから止まり、行ごとの再生で次の行の頭が鳴ってしまう (タブが裏だと止まらない)
  const playerRef = useRef<RangeAudioPlayer | null>(null);
  const chunkRef = useRef(chunk);
  chunkRef.current = chunk;
  const getPlayer = useCallback(() => {
    if (!playerRef.current) playerRef.current = new RangeAudioPlayer(() => loadChunkAudioBytes(chunkRef.current));
    return playerRef.current;
  }, []);
  const rafRef = useRef<number | null>(null);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState<RangePlayback | null>(null);

  const stopLoop = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };
  const stop = useCallback(() => {
    stopLoop();
    playerRef.current?.stop();
    setPlaying(null);
  }, []);
  useEffect(() => {
    // 開いた時点で裏でデコードし始め、初回再生の待ちを消す
    if (RangeAudioPlayer.isSupported()) void getPlayer().preload();
    return () => {
      stopLoop();
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, [getPlayer]);

  const play = useCallback(async (start: number, end: number | null) => {
    stop();
    if (!RangeAudioPlayer.isSupported()) {
      toast.error('このブラウザでは音声を再生できません');
      return;
    }
    const player = getPlayer();
    const pb: RangePlayback = { start, end };
    const result = await player.play(start, end, () => {
      // 区間を鳴らし終えた (stop() で止めたときは呼ばれない)
      stopLoop();
      setPlaying(null);
      setPlayhead(end ?? total);
    });
    if (result === 'superseded') return;
    if (result === 'unavailable') {
      toast.error('音声を再生できませんでした');
      return;
    }
    setPlaying(pb);
    const tick = () => {
      const t = player.position();
      if (t === null) {
        rafRef.current = null;
        return;
      }
      setPlayhead(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [getPlayer, stop, toast, total]);

  const toggleAll = () => {
    if (playing) stop();
    else void play(playhead >= total - 0.05 ? 0 : playhead, null);
  };

  // --- 境界のドラッグ・キー操作 ---
  const secAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || pxPerSec <= 0) return 0;
    return Math.min(total, Math.max(0, (clientX - rect.left) / pxPerSec));
  };
  const onHandlePointerDown = (index: number) => (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCut(index);
    e.currentTarget.focus();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => setCuts((prev) => moveCut(prev, index, secAt(ev.clientX), total));
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };
  const onHandleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = (e.shiftKey ? NUDGE_FINE_SEC : NUDGE_SEC) * (e.key === 'ArrowLeft' ? -1 : 1);
    setCuts((prev) => moveCut(prev, index, prev[index] + step, total));
  };
  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = secAt(e.clientX);
    setPlayhead(t);
    if (playing) void play(t, null);
  };

  // 選択中の境界が画面外なら見える位置へスクロール (拡大時)
  useEffect(() => {
    if (selectedCut === null || !scrollRef.current) return;
    const x = cuts[selectedCut] * pxPerSec;
    const el = scrollRef.current;
    if (x < el.scrollLeft + 24 || x > el.scrollLeft + el.clientWidth - 24) el.scrollLeft = Math.max(0, x - el.clientWidth / 2);
    // 拡大率を変えたときだけ追従する (ドラッグ中に勝手に動かさない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const previewBoundary = () => {
    if (selectedCut === null) return;
    const c = cuts[selectedCut];
    void play(Math.max(0, c - BOUNDARY_PREVIEW_SEC), Math.min(total, c + BOUNDARY_PREVIEW_SEC));
  };

  const apply = async () => {
    if (changed.length === 0 || saving) return;
    stop();
    setSaving(true);
    try {
      const result = await resplitVoiceLines(scenarioId, { full_material_id: chunk.full_material_id, cuts });
      await refreshVoiceScenario?.(scenarioId);
      void queryClient.invalidateQueries({ queryKey: voiceLinesSplitQueryKey(scenarioId) });
      void queryClient.invalidateQueries({ queryKey: scenarioVoiceMapQueryKey(scenarioId) });
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenarioId) });
      if (result.failed.length > 0) {
        const lines = result.failed.map((f) => `シーン${f.order ?? '?'}: ${f.error}`);
        toast.error(`${result.scenes.length}シーンのナレーションの区切りを調整しました。切り直せなかったシーンがあります\n${lines.join('\n')}`);
      } else {
        toast.success(`${result.scenes.length}シーンのナレーションの区切りを調整しました`);
      }
      const advice = result.plan?.status === 'advice' ? result.plan.advice : null;
      if (advice) toast.show(`ナレーションがシーンの尺に収まりません: ${typeof advice === 'string' ? advice : advice.message ?? ''}`);
      onClose();
    } catch (err) {
      if (isStorageQuotaError(err)) {
        toast.error(storageQuotaErrorMessage());
      } else {
        const axiosErr = err as AxiosError<{ error?: string }>;
        toast.error(axiosErr.response?.data?.error || '分割位置の調整に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  const lineLabel = (i: number) => {
    const line = chunk.lines[i];
    if (!line) return '';
    if (line.order == null) return '削除済み';
    return line.speaker_label ? `シーン${line.order}・${line.speaker_label}` : `シーン${line.order}`;
  };

  return (
    <>
      <div className="modal-body">
        <p className={own.hint}>
          縦線をドラッグして行の区切りを調整します。縦線を選んで ←/→ で {NUDGE_SEC} 秒ずつ（Shift で {NUDGE_FINE_SEC} 秒）動かせます。
          適用すると区切りを動かした行を切り直し、その行を含むシーンのナレーションを組み立て直します（ポイントは消費しません・元の音声は履歴に残ります）。
        </p>

        <div className={own.toolbar}>
          <button type="button" className="btn-modal-secondary-sm" onClick={toggleAll}>
            {playing && playing.end === null ? '停止' : '全体を再生'}
          </button>
          <button
            type="button"
            className="btn-modal-secondary-sm"
            onClick={previewBoundary}
            disabled={selectedCut === null}
            title={selectedCut === null ? '縦線を選ぶと試聴できます' : undefined}
          >
            区切りの前後を試聴
          </button>
          <span className={own.time}>{formatSplitTime(playhead)} / {formatSplitTime(total)}</span>
          <div className={own.zoom}>
            <button type="button" className={own.zoomBtn} onClick={() => setZoomIdx((z) => Math.max(0, Math.min(z, maxZoomIdx) - 1))} disabled={zoom <= 1} aria-label="縮小">−</button>
            <span className={own.zoomLabel}>{zoom}x</span>
            <button type="button" className={own.zoomBtn} onClick={() => setZoomIdx((z) => Math.min(maxZoomIdx, z + 1))} disabled={zoomIdx >= maxZoomIdx} aria-label="拡大">＋</button>
          </div>
        </div>

        <div ref={scrollRef} className={own.scroll}>
          <div ref={trackRef} className={own.track} style={{ width: trackWidth, height: WAVEFORM_HEIGHT + 22 }} onClick={onTrackClick}>
            {/* 行の区間 (交互の色・変更した行は強調) */}
            {ranges.map(([start, end], i) => (
              <div
                key={`${chunk.lines[i].scene_id}:${chunk.lines[i].line_id}`}
                className={`${own.region} ${i % 2 ? own.regionOdd : ''} ${changedSet.has(i) ? own.regionChanged : ''} ${missing[i] ? own.regionMissing : ''}`}
                style={{ left: start * pxPerSec, width: Math.max(0, (end - start) * pxPerSec) }}
              >
                <span className={own.regionLabel}>{lineLabel(i)}</span>
              </div>
            ))}
            <canvas ref={canvasRef} className={own.canvas} style={{ width: trackWidth, height: WAVEFORM_HEIGHT }} />
            {/* 再生位置 */}
            <div className={own.playhead} style={{ left: playhead * pxPerSec }} />
            {/* 区切り (境界) */}
            {cuts.map((c, i) => (
              <button
                key={i}
                type="button"
                role="slider"
                aria-label={`${lineLabel(i)}と${lineLabel(i + 1)}の区切り`}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={c}
                aria-valuetext={formatSplitTime(c)}
                className={`${own.handle} ${selectedCut === i ? own.handleSelected : ''}`}
                style={{ left: c * pxPerSec }}
                onPointerDown={onHandlePointerDown(i)}
                onKeyDown={onHandleKeyDown(i)}
                onFocus={() => setSelectedCut(i)}
                onClick={(e) => e.stopPropagation()}
              >
                <span className={own.handleLine} />
                <span className={own.handleTime}>{formatSplitTime(c)}</span>
              </button>
            ))}
          </div>
        </div>

        <ul className={own.sceneList}>
          {chunk.lines.map((line, i) => {
            const [start, end] = ranges[i];
            const isPlaying = !!playing && playing.start === start && playing.end === end;
            return (
              <li key={`${line.scene_id}:${line.line_id}`} className={`${own.sceneRow} ${changedSet.has(i) ? own.sceneRowChanged : ''}`}>
                <button
                  type="button"
                  className={own.scenePlay}
                  onClick={() => (isPlaying ? stop() : void play(start, end))}
                  aria-label={isPlaying ? '停止' : `${lineLabel(i)}を再生`}
                >
                  {isPlaying ? '■' : '▶'}
                </button>
                <div className={own.sceneMain}>
                  <div className={own.sceneHead}>
                    <span className={own.sceneName}>{lineLabel(i)}</span>
                    <span className={own.sceneTime}>
                      {formatSplitTime(start)} – {formatSplitTime(end)}（{(end - start).toFixed(2)}秒）
                    </span>
                    {changedSet.has(i) && <span className={own.badgeChanged}>変更あり</span>}
                    {line.missing && <span className={own.badgeMuted}>{line.order == null ? 'シーン削除済み・割り当てません' : '台本から消えた行・割り当てません'}</span>}
                    {!line.missing && !line.assigned && (
                      <span className={own.badgeMuted} title="区切りを動かすと、まとめて読んだ音声を切り直して割り当てます">
                        別に生成した音声を使用中
                      </span>
                    )}
                  </div>
                  <p className={own.sceneText}>{line.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className={own.footer}>
        <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>キャンセル</button>
        <div className={own.footerRight}>
          <button type="button" className="btn-modal-secondary" onClick={() => setCuts(initialCuts)} disabled={saving || changed.length === 0}>
            元に戻す
          </button>
          <button type="button" className="btn-modal" onClick={() => void apply()} disabled={saving || changed.length === 0}>
            {saving ? '切り直し中…' : changed.length > 0 ? `適用（${changedSceneCount}シーン）` : '適用'}
          </button>
        </div>
      </div>
    </>
  );
}
